/**
 * Memory pattern detection — recognizes struct field access and array indexing
 * in Expression trees.
 *
 * Struct pattern: load/store at base + constant_offset → base->field_N
 * Array pattern: load/store at base + (index << N) → base[index]
 */

import { Expression, Statement, LoweredNode } from './ExpressionIR';

/**
 * Transform memory access expressions in a lowered tree.
 * Rewrites load expressions:
 * - load(binary(+, var, const)) → load with named offset (struct-like)
 * - load(binary(+, var, binary(<<, index, const))) → array-like
 */
interface ArrayAccess {
  base: Expression;
  index: Expression;
}

function extractArrayIndex(expression: Expression): Expression | null {
  // index << N (power-of-2 element size)
  if (expression.kind === 'binary' && expression.op === '<<' && expression.right.kind === 'const') {
    const shift = Number(expression.right.value);
    if (shift >= 1 && shift <= 4) {
      return expression.left;
    }
  }
  // index * elementSize (any element size >= 2)
  if (expression.kind === 'binary' && expression.op === '*') {
    if (expression.right.kind === 'const') {
      const elementSize = Number(expression.right.value);
      if (elementSize >= 2 && elementSize <= 256) {
        return expression.left;
      }
    }
    if (expression.left.kind === 'const') {
      const elementSize = Number(expression.left.value);
      if (elementSize >= 2 && elementSize <= 256) {
        return expression.right;
      }
    }
  }
  return null;
}

/**
 * Recognizes `base + (index * elementSize)` in either operand order and reports
 * which operand is the base and which is the (unscaled) index. The scaled term
 * may be on the left or the right; the base is always the other operand.
 */
function extractArrayAccess(address: Expression): ArrayAccess | null {
  if (address.kind !== 'binary' || address.op !== '+') {
    return null;
  }
  const indexFromRight = extractArrayIndex(address.right);
  if (indexFromRight) {
    return { base: address.left, index: indexFromRight };
  }
  const indexFromLeft = extractArrayIndex(address.left);
  if (indexFromLeft) {
    return { base: address.right, index: indexFromLeft };
  }
  return null;
}

export function annotateMemoryPatterns(node: LoweredNode, frameVarName?: string | null): LoweredNode {
  // Pass 1: collect struct pointer candidates (variables used as base with 2+ distinct offsets)
  const baseOffsets = new Map<string, Set<number>>();
  collectMemoryBases(node, baseOffsets);
  // A variable qualifies as a struct pointer if it is used as a memory base
  // with at least 2 distinct constant offsets, indicating field-like access patterns.
  // Exclude the stack frame pointer — it's a buffer, not a struct.
  const structBases = new Set<string>();
  for (const [baseName, offsets] of baseOffsets) {
    if (offsets.size >= 2 && baseName !== frameVarName) {
      structBases.add(baseName);
    }
  }
  // Pass 2: transform patterns
  return transformNode(node, structBases);
}

function collectMemoryBases(node: LoweredNode, bases: Map<string, Set<number>>): void {
  switch (node.kind) {
    case 'sequence': for (const child of node.children) { collectMemoryBases(child, bases); } break;
    case 'block': for (const statement of node.body) { collectBasesFromStmt(statement, bases); } break;
    case 'if': collectMemoryBases(node.thenBody, bases); if (node.elseBody) { collectMemoryBases(node.elseBody, bases); } break;
    case 'while': collectMemoryBases(node.body, bases); break;
    case 'do_while': collectMemoryBases(node.body, bases); break;
    case 'for': collectMemoryBases(node.body, bases); break;
    case 'switch': for (const caseEntry of node.cases) { collectMemoryBases(caseEntry.body, bases); } collectMemoryBases(node.defaultBody, bases); break;
    case 'labeled_block': collectMemoryBases(node.body, bases); break;
  }
}

function collectBasesFromStmt(statement: Statement, bases: Map<string, Set<number>>): void {
  switch (statement.kind) {
    case 'assign': collectBasesFromExpr(statement.value, bases); break;
    case 'store': collectBasesFromExpr(statement.address, bases); collectBasesFromExpr(statement.value, bases); recordMemoryBase(statement.address, statement.offset, bases); break;
    case 'call': for (const arg of statement.args) { collectBasesFromExpr(arg, bases); } break;
    case 'call_indirect': collectBasesFromExpr(statement.tableIndex, bases); for (const arg of statement.args) { collectBasesFromExpr(arg, bases); } break;
    case 'global_set': collectBasesFromExpr(statement.value, bases); break;
    case 'return': for (const value of statement.values) { collectBasesFromExpr(value, bases); } break;
  }
}

function collectBasesFromExpr(expression: Expression, bases: Map<string, Set<number>>): void {
  if (expression.kind === 'load') {
    collectBasesFromExpr(expression.address, bases);
    recordMemoryBase(expression.address, expression.offset, bases);
  } else if (expression.kind === 'binary') {
    collectBasesFromExpr(expression.left, bases);
    collectBasesFromExpr(expression.right, bases);
  } else if (expression.kind === 'unary') {
    collectBasesFromExpr(expression.operand, bases);
  } else if (expression.kind === 'compare') {
    collectBasesFromExpr(expression.left, bases);
    collectBasesFromExpr(expression.right, bases);
  } else if (expression.kind === 'select') {
    collectBasesFromExpr(expression.condition, bases);
    collectBasesFromExpr(expression.trueVal, bases);
    collectBasesFromExpr(expression.falseVal, bases);
  } else if (expression.kind === 'call') {
    for (const arg of expression.args) { collectBasesFromExpr(arg, bases); }
  } else if (expression.kind === 'convert') {
    collectBasesFromExpr(expression.operand, bases);
  } else if (expression.kind === 'field_access') {
    collectBasesFromExpr(expression.base, bases);
  }
}

function recordMemoryBase(address: Expression, instrOffset: number, bases: Map<string, Set<number>>): void {
  // Pattern: var + const
  if (address.kind === 'binary' && address.op === '+' && address.left.kind === 'var' && address.right.kind === 'const') {
    const baseName = address.left.name;
    const offset = Number(address.right.value) + instrOffset;
    if (!bases.has(baseName)) { bases.set(baseName, new Set()); }
    bases.get(baseName)!.add(offset);
  }
  // Pattern: bare var with an instruction offset — one access at one offset.
  // Record only the real offset; struct classification needs 2+ distinct ones.
  if (address.kind === 'var') {
    const baseName = address.name;
    if (!bases.has(baseName)) { bases.set(baseName, new Set()); }
    bases.get(baseName)!.add(instrOffset);
  }
}

function transformNode(node: LoweredNode, structBases: Set<string>): LoweredNode {
  const mapNode = (childNode: LoweredNode) => transformNode(childNode, structBases);
  const mapExpression = (expression: Expression) => transformExpr(expression, structBases);
  const mapStatement = (statement: Statement) => transformStatement(statement, structBases);
  switch (node.kind) {
    case 'sequence':
      return { kind: 'sequence', children: node.children.map(mapNode) };
    case 'block':
      return { kind: 'block', body: node.body.map(mapStatement) };
    case 'if':
      return { kind: 'if', condition: mapExpression(node.condition), thenBody: mapNode(node.thenBody), elseBody: node.elseBody ? mapNode(node.elseBody) : null };
    case 'while':
      return { kind: 'while', condition: node.condition ? mapExpression(node.condition) : null, body: mapNode(node.body) };
    case 'do_while':
      return { kind: 'do_while', body: mapNode(node.body), condition: mapExpression(node.condition) };
    case 'for':
      return { kind: 'for', init: mapStatement(node.init), condition: mapExpression(node.condition), increment: mapStatement(node.increment), body: mapNode(node.body) };
    case 'switch':
      return { kind: 'switch', selector: mapExpression(node.selector), cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: mapNode(caseEntry.body) })), defaultBody: mapNode(node.defaultBody) };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: mapNode(node.body) };
    default:
      return node;
  }
}

function transformStatement(statement: Statement, structBases: Set<string>): Statement {
  const mapExpression = (expression: Expression) => transformExpr(expression, structBases);
  switch (statement.kind) {
    case 'assign':
      return { kind: 'assign', target: statement.target, type: statement.type, value: mapExpression(statement.value) };
    case 'store': {
      const address = mapExpression(statement.address);
      const value = mapExpression(statement.value);
      // Skip struct/array pattern detection for v128 and atomic stores
      if (statement.storeType.includes('v128') || statement.storeType.includes('atomic')) {
        return { kind: 'store', address, offset: statement.offset, storeType: statement.storeType, value };
      }
      // Struct field store: base + const where base is a struct pointer
      const structStore = tryStructAccess(address, statement.offset, structBases);
      if (structStore) {
        return { kind: 'store', address: structStore, offset: 0, storeType: statement.storeType, value };
      }
      // Array store: base + (index << N) or base + (index * elementSize)
      const arrayStore = extractArrayAccess(address);
      if (arrayStore) {
        return { kind: 'store', address: { kind: 'binary', op: '[]', left: arrayStore.base, right: arrayStore.index }, offset: statement.offset, storeType: statement.storeType, value };
      }
      return { kind: 'store', address, offset: statement.offset, storeType: statement.storeType, value };
    }
    case 'call':
      return { kind: 'call', name: statement.name, args: statement.args.map(mapExpression), result: statement.result };
    case 'call_indirect':
      return { kind: 'call_indirect', tableIndex: mapExpression(statement.tableIndex), args: statement.args.map(mapExpression), result: statement.result };
    case 'global_set':
      return { kind: 'global_set', name: statement.name, value: mapExpression(statement.value) };
    case 'return':
      return { kind: 'return', values: statement.values.map(mapExpression) };
    default:
      return statement;
  }
}

function tryStructAccess(address: Expression, instrOffset: number, structBases: Set<string>): Expression | null {
  // Pattern: var + const where var is a struct base
  if (address.kind === 'binary' && address.op === '+' && address.left.kind === 'var' && address.right.kind === 'const') {
    if (structBases.has(address.left.name)) {
      const totalOffset = Number(address.right.value) + instrOffset;
      return { kind: 'field_access', base: address.left, offset: totalOffset };
    }
  }
  // Pattern: var with instrOffset > 0
  if (address.kind === 'var' && instrOffset > 0 && structBases.has(address.name)) {
    return { kind: 'field_access', base: address, offset: instrOffset };
  }
  return null;
}

function transformExpr(expression: Expression, structBases: Set<string>): Expression {
  const mapExpression = (childExpr: Expression) => transformExpr(childExpr, structBases);
  switch (expression.kind) {
    case 'load': {
      const address = mapExpression(expression.address);
      // Skip struct/array pattern detection for v128 and atomic loads
      if (expression.loadType.includes('v128') || expression.loadType.includes('atomic')) {
        return { kind: 'load', address, offset: expression.offset, loadType: expression.loadType };
      }
      // Struct field access: base + const where base is a struct pointer
      const structAccess = tryStructAccess(address, expression.offset, structBases);
      if (structAccess) {
        return { kind: 'load', address: structAccess, offset: 0, loadType: expression.loadType };
      }
      // Array access: base + (index << N) or base + (index * elementSize)
      const arrayAccess = extractArrayAccess(address);
      if (arrayAccess) {
        return { kind: 'load', address: { kind: 'binary', op: '[]', left: arrayAccess.base, right: arrayAccess.index }, offset: expression.offset, loadType: expression.loadType };
      }
      return { kind: 'load', address, offset: expression.offset, loadType: expression.loadType };
    }
    case 'binary':
      return { kind: 'binary', op: expression.op, left: mapExpression(expression.left), right: mapExpression(expression.right) };
    case 'unary':
      return { kind: 'unary', op: expression.op, operand: mapExpression(expression.operand) };
    case 'compare':
      return { kind: 'compare', op: expression.op, left: mapExpression(expression.left), right: mapExpression(expression.right) };
    case 'select':
      return { kind: 'select', condition: mapExpression(expression.condition), trueVal: mapExpression(expression.trueVal), falseVal: mapExpression(expression.falseVal) };
    case 'convert':
      return { kind: 'convert', op: expression.op, operand: mapExpression(expression.operand) };
    case 'call':
      return { kind: 'call', name: expression.name, args: expression.args.map(mapExpression) };
    case 'call_indirect':
      return { kind: 'call_indirect', tableIndex: mapExpression(expression.tableIndex), args: expression.args.map(mapExpression) };
    case 'field_access':
      return { kind: 'field_access', base: mapExpression(expression.base), offset: expression.offset };
    default:
      return expression;
  }
}
